<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useAuth0 } from "@auth0/auth0-vue";
import { useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";

import { getMe } from "@/api/client";
import { MANAGEMENT_ROLES } from "@/lib/permissions";
import { isWorkforceRoute } from "@/modules";
import { useProjectStore } from "@/stores/project";
import ProjectSearchOverlay from "@/components/ProjectSearchOverlay.vue";

defineEmits<{ (e: "toggle-drawer"): void }>();

const { logout } = useAuth0();
const route = useRoute();
const { selectedProjectId } = storeToRefs(useProjectStore());

const projectId = computed(() => route.params.projectId as string | undefined);
const searchOpen = ref(false);

function onKey(e: KeyboardEvent): void {
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k" && projectId.value) {
    e.preventDefault();
    searchOpen.value = true;
  }
}
onMounted(() => window.addEventListener("keydown", onKey));
onUnmounted(() => window.removeEventListener("keydown", onKey));

const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });

const inWorkforce = computed(() => isWorkforceRoute(route.path));
const onProjectsList = computed(() => route.name === "projects");
// User administration — management only.
const isManagement = computed(() =>
  (me.value?.roles ?? []).some((r) => MANAGEMENT_ROLES.includes(r)),
);
// Inside a project = a project route that isn't the list and isn't workforce.
const inProject = computed(
  () => !!route.params.projectId && !onProjectsList.value,
);
const currentProject = computed(() =>
  (me.value?.projects ?? []).find((p) => p.id === selectedProjectId.value),
);

function signOut(): void {
  void logout({ logoutParams: { returnTo: window.location.origin } });
}
</script>

<template>
  <header
    class="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-neutral-200 bg-neutral-0 px-4 sm:px-6 print:hidden"
  >
    <button
      v-if="!onProjectsList"
      class="-ml-1 rounded-lg p-2 text-neutral-600 hover:bg-neutral-50 lg:hidden"
      type="button"
      aria-label="Open navigation"
      @click="$emit('toggle-drawer')"
    >
      <svg
        class="h-6 w-6"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        viewBox="0 0 24 24"
      >
        <path d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>

    <RouterLink
      :to="{ name: 'projects' }"
      class="shrink-0"
    >
      <img
        src="/ecs-logo.svg"
        alt="ECS Group Command"
        class="h-6 w-auto"
      >
    </RouterLink>

    <!-- Top-level areas -->
    <nav class="ml-4 hidden items-center gap-1 md:flex">
      <RouterLink
        :to="{ name: 'projects' }"
        class="rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
        :class="(onProjectsList || inProject) ? 'bg-brand-primary/10 text-brand-primary' : ''"
      >
        Projects
      </RouterLink>
      <RouterLink
        to="/workers"
        class="rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
        :class="inWorkforce ? 'bg-brand-primary/10 text-brand-primary' : ''"
      >
        Workforce
      </RouterLink>
      <RouterLink
        v-if="isManagement"
        to="/users"
        class="rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
        :class="$route.name === 'users-admin' ? 'bg-brand-primary/10 text-brand-primary' : ''"
      >
        Users
      </RouterLink>
    </nav>

    <!-- Current project context (a link back to the projects list) -->
    <div
      v-if="inProject && currentProject"
      class="ml-2 hidden items-center gap-2 text-sm text-neutral-400 sm:flex"
    >
      <span>/</span>
      <RouterLink
        :to="{ name: 'projects' }"
        class="font-medium text-neutral-700 hover:text-brand-primary"
      >
        {{ currentProject.name }}
      </RouterLink>
    </div>

    <div class="ml-auto flex items-center gap-1">
      <button
        v-if="inProject && projectId"
        type="button"
        class="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
        title="Search this project (⌘K)"
        @click="searchOpen = true"
      >
        <svg
          class="h-5 w-5 shrink-0"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
        >
          <circle
            cx="11"
            cy="11"
            r="7"
          />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <span class="hidden md:inline">Search</span>
      </button>
      <RouterLink
        to="/help"
        class="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
        :class="$route.name === 'help' ? 'bg-brand-primary/10 text-brand-primary' : ''"
        title="Help & guides"
      >
        <svg
          class="h-5 w-5 shrink-0"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          viewBox="0 0 24 24"
        >
          <circle
            cx="12"
            cy="12"
            r="9"
          />
          <path d="M9.5 9a2.5 2.5 0 015 0c0 1.5-1.5 2-2.5 2.8V13" />
          <path d="M12 16.5h.01" />
        </svg>
        <span class="hidden sm:inline">Help</span>
      </RouterLink>
      <RouterLink
        to="/account"
        class="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
        :class="$route.name === 'account' ? 'bg-brand-primary/10 text-brand-primary' : ''"
        title="My account"
      >
        <svg
          class="h-5 w-5 shrink-0"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          viewBox="0 0 24 24"
        >
          <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
          <circle
            cx="9"
            cy="7"
            r="4"
          />
        </svg>
        <span class="hidden sm:inline">Account</span>
      </RouterLink>
      <button
        class="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
        type="button"
        @click="signOut"
      >
        <svg
          class="h-5 w-5 shrink-0"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          viewBox="0 0 24 24"
        >
          <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
        </svg>
        <span class="hidden sm:inline">Sign out</span>
      </button>
    </div>
  </header>

  <ProjectSearchOverlay
    v-if="projectId"
    :project-id="projectId"
    :open="searchOpen"
    @close="searchOpen = false"
  />
</template>
